import { useCallback, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { getApiErrorMessage } from "@/lib/errors";
import type { LinkableEntity } from "@/components/directory/LinkEntityPopover";
import type {
    ClaimContact,
    ClaimOrganization,
    ClaimProperty,
    ClaimPropertyAddressTypeEnum,
    ClaimDetailed,
    Contact,
    CreateContactRequest,
    UpdateContactRequest,
    Organization,
    CreateOrganizationRequest,
    UpdateOrganizationRequest,
    Property,
    CreatePropertyRequest,
    UpdatePropertyRequest,
} from "@/api/generated/client/models";

const PAGE_SIZE = 100;

// Loads every page of an offset-paginated list endpoint so counts, rows, and
// existingIds reflect the full set instead of only the first page.
async function fetchAllPages<T>(
    fetchPage: (limit: number, offset: number) => Promise<{ items: T[]; total: number }>,
): Promise<T[]> {
    const all: T[] = [];
    let offset = 0;
    let total = Infinity;
    while (all.length < total) {
        const { items, total: pageTotal } = await fetchPage(PAGE_SIZE, offset);
        all.push(...items);
        total = pageTotal;
        if (items.length === 0) break;
        offset += PAGE_SIZE;
    }
    return all;
}

// ===============================
// Claim ↔ Contacts
// ===============================

export function useClaimContacts(claimId: number | null, enabled = true) {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const { data, isLoading } = useQuery({
        queryKey: ["claim-contacts", claimId],
        queryFn: async () => {
            if (!claimId) return [] as ClaimContact[];
            return fetchAllPages<ClaimContact>(async (limit, offset) => {
                const res = await apiClient.claims.getClaimContacts({ claimId, limit, offset });
                return { items: res.claimContacts ?? [], total: res.meta?.total ?? 0 };
            });
        },
        enabled: !!claimId && enabled,
    });

    const invalidate = () => queryClient.invalidateQueries({ queryKey: ["claim-contacts", claimId] });

    const linkMutation = useMutation({
        mutationFn: async ({ contactId, role }: { contactId: number; role: string }) => {
            if (claimId == null) throw new Error("Cannot link contact: claimId is not set");
            await apiClient.claims.linkClaimContact({ claimId, linkClaimContactRequest: { contactId, role } });
        },
        onSuccess: () => { invalidate(); toast({ title: "Contact linked" }); },
        onError: async (error: unknown) => {
            toast({ title: "Failed to link contact", description: await getApiErrorMessage(error), variant: "destructive" });
        },
    });

    const unlinkMutation = useMutation({
        mutationFn: async (contactId: number) => {
            if (claimId == null) throw new Error("Cannot unlink contact: claimId is not set");
            await apiClient.claims.unlinkClaimContact({ claimId, contactId });
        },
        onSuccess: () => { invalidate(); toast({ title: "Contact unlinked" }); },
        onError: async (error: unknown) => {
            toast({ title: "Failed to unlink contact", description: await getApiErrorMessage(error), variant: "destructive" });
        },
    });

    return {
        contacts: data ?? [],
        isLoading,
        linkContact: linkMutation.mutateAsync,
        unlinkContact: unlinkMutation.mutate,
        invalidate,
    };
}

// ===============================
// Claim ↔ Organizations
// ===============================

export function useClaimOrganizations(claimId: number | null, enabled = true) {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const { data, isLoading } = useQuery({
        queryKey: ["claim-organizations", claimId],
        queryFn: async () => {
            if (!claimId) return [] as ClaimOrganization[];
            return fetchAllPages<ClaimOrganization>(async (limit, offset) => {
                const res = await apiClient.claims.getClaimOrganizations({ claimId, limit, offset });
                return { items: res.claimOrganizations ?? [], total: res.meta?.total ?? 0 };
            });
        },
        enabled: !!claimId && enabled,
    });

    const invalidate = () => queryClient.invalidateQueries({ queryKey: ["claim-organizations", claimId] });

    const linkMutation = useMutation({
        mutationFn: async ({ organizationId, role }: { organizationId: number; role: string }) => {
            if (claimId == null) throw new Error("Cannot link organization: claimId is not set");
            await apiClient.claims.linkClaimOrganization({ claimId, linkClaimOrganizationRequest: { organizationId, role } });
        },
        onSuccess: () => { invalidate(); toast({ title: "Organization linked" }); },
        onError: async (error: unknown) => {
            toast({ title: "Failed to link organization", description: await getApiErrorMessage(error), variant: "destructive" });
        },
    });

    const unlinkMutation = useMutation({
        mutationFn: async (organizationId: number) => {
            if (claimId == null) throw new Error("Cannot unlink organization: claimId is not set");
            await apiClient.claims.unlinkClaimOrganization({ claimId, organizationId });
        },
        onSuccess: () => { invalidate(); toast({ title: "Organization unlinked" }); },
        onError: async (error: unknown) => {
            toast({ title: "Failed to unlink organization", description: await getApiErrorMessage(error), variant: "destructive" });
        },
    });

    return {
        organizations: data ?? [],
        isLoading,
        linkOrganization: linkMutation.mutateAsync,
        unlinkOrganization: unlinkMutation.mutate,
        invalidate,
    };
}

// ===============================
// Claim ↔ Properties
// ===============================

export function useClaimProperties(claimId: number | null) {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const { data, isLoading } = useQuery({
        queryKey: ["claim-properties", claimId],
        queryFn: async () => {
            if (!claimId) return [] as ClaimProperty[];
            return fetchAllPages<ClaimProperty>(async (limit, offset) => {
                const res = await apiClient.claims.getClaimProperties({ claimId, limit, offset });
                return { items: res.claimProperties ?? [], total: res.meta?.total ?? 0 };
            });
        },
        enabled: !!claimId,
    });

    const invalidate = () => queryClient.invalidateQueries({ queryKey: ["claim-properties", claimId] });

    const linkMutation = useMutation({
        mutationFn: async (propertyId: number) => {
            if (claimId == null) throw new Error("Cannot link property: claimId is not set");
            await apiClient.claims.linkClaimProperty({ claimId, linkClaimPropertyRequest: { propertyId } });
        },
        onSuccess: () => { invalidate(); toast({ title: "Property linked" }); },
        onError: async (error: unknown) => {
            toast({ title: "Failed to link property", description: await getApiErrorMessage(error), variant: "destructive" });
        },
    });

    const unlinkMutation = useMutation({
        mutationFn: async ({ propertyId, addressType }: { propertyId: number; addressType?: ClaimPropertyAddressTypeEnum }) => {
            if (claimId == null) throw new Error("Cannot unlink property: claimId is not set");
            await apiClient.claims.unlinkClaimProperty({ claimId, propertyId, addressType });
        },
        onSuccess: () => { invalidate(); toast({ title: "Property unlinked" }); },
        onError: async (error: unknown) => {
            toast({ title: "Failed to unlink property", description: await getApiErrorMessage(error), variant: "destructive" });
        },
    });

    return {
        properties: data ?? [],
        isLoading,
        linkProperty: linkMutation.mutateAsync,
        unlinkProperty: unlinkMutation.mutate,
        invalidate,
    };
}

// ===============================
// Directory orchestration (for the claim sidebar section)
// ===============================

export interface DirectoryLinkItem {
    key: number;
    label: string;
    badge?: string | null;
    onNavigate?: () => void;
    onUnlink?: () => void;
}

export interface DirectoryGroupData {
    items: DirectoryLinkItem[];
    existingIds: number[];
    onSearch: (query: string) => Promise<LinkableEntity[]>;
    onSelect: (id: number) => Promise<unknown> | void;
    onCreateNew: () => void;
}

interface DirectoryEntityDialogs<TEntity, TCreate, TCreated, TUpdate> {
    createOpen: boolean;
    setCreateOpen: (open: boolean) => void;
    create: (data: TCreate) => Promise<TCreated>;
    isCreating: boolean;
    onCreated: () => void;
    detail: TEntity | null;
    setDetail: (entity: TEntity | null) => void;
    detailIsLocal: boolean;
    setDetailIsLocal: (isLocal: boolean) => void;
    edit: TEntity | null;
    setEdit: (entity: TEntity | null) => void;
    editIsLocal: boolean;
    setEditIsLocal: (isLocal: boolean) => void;
    update: (args: { id: number; data: TUpdate }) => Promise<TEntity>;
    isUpdating: boolean;
}

export interface ClaimDirectory {
    canWrite: boolean;
    canManage: boolean;
    contacts: DirectoryGroupData;
    organizations: DirectoryGroupData;
    properties: DirectoryGroupData;
    dialogs: {
        contact: DirectoryEntityDialogs<Contact, CreateContactRequest, Contact, UpdateContactRequest>;
        organization: DirectoryEntityDialogs<Organization, CreateOrganizationRequest, Organization, UpdateOrganizationRequest>;
        property: DirectoryEntityDialogs<Property, CreatePropertyRequest, Property, UpdatePropertyRequest>;
    };
}

/**
 * Consolidates the data, mutations, search, and dialog state that the claim
 * sidebar "Directory" section needs into a single hook.
 */
export function useClaimDirectory(claim: ClaimDetailed): ClaimDirectory {
    const claimId = claim.id;
    const canWrite = claim.canWrite ?? false;
    const { toast } = useToast();
    const { user } = useAuth();
    const viewerCompanyId = user?.companyId;
    const canManage = canWrite;

    const {
        contacts,
        linkContact,
        unlinkContact,
        invalidate: invalidateContacts,
    } = useClaimContacts(claimId);
    const {
        organizations,
        linkOrganization,
        unlinkOrganization,
        invalidate: invalidateOrganizations,
    } = useClaimOrganizations(claimId);
    const {
        properties,
        linkProperty,
        unlinkProperty,
        invalidate: invalidateProperties,
    } = useClaimProperties(claimId);

    // Dialog / drawer state.
    const [contactCreateOpen, setContactCreateOpen] = useState(false);
    const [orgCreateOpen, setOrgCreateOpen] = useState(false);
    const [propertyCreateOpen, setPropertyCreateOpen] = useState(false);
    const [detailContact, setDetailContact] = useState<Contact | null>(null);
    const [detailOrg, setDetailOrg] = useState<Organization | null>(null);
    const [detailProperty, setDetailProperty] = useState<Property | null>(null);
    const [detailContactIsLocal, setDetailContactIsLocal] = useState(false);
    const [detailOrgIsLocal, setDetailOrgIsLocal] = useState(false);
    const [detailPropertyIsLocal, setDetailPropertyIsLocal] = useState(false);
    const [editContact, setEditContact] = useState<Contact | null>(null);
    const [editOrg, setEditOrg] = useState<Organization | null>(null);
    const [editProperty, setEditProperty] = useState<Property | null>(null);
    const [editContactIsLocal, setEditContactIsLocal] = useState(false);
    const [editOrgIsLocal, setEditOrgIsLocal] = useState(false);
    const [editPropertyIsLocal, setEditPropertyIsLocal] = useState(false);

    // Cache search-result types so linking an existing entity reuses its type as the role.
    const contactTypeCache = useRef<Map<number, string>>(new Map());
    const orgTypeCache = useRef<Map<number, string>>(new Map());

    const createContactMutation = useMutation({
        mutationFn: (data: CreateContactRequest) =>
            apiClient.contacts.createContact({ createContactRequest: data }),
        onError: async (error: unknown) => {
            toast({ title: "Failed to create contact", description: await getApiErrorMessage(error), variant: "destructive" });
        },
    });
    const createOrgMutation = useMutation({
        mutationFn: (data: CreateOrganizationRequest) =>
            apiClient.organizations.createOrganization({ createOrganizationRequest: data }),
        onError: async (error: unknown) => {
            toast({ title: "Failed to create organization", description: await getApiErrorMessage(error), variant: "destructive" });
        },
    });
    const createPropertyMutation = useMutation({
        mutationFn: (data: CreatePropertyRequest) =>
            apiClient.properties.createProperty({ createPropertyRequest: data }),
        onError: async (error: unknown) => {
            toast({ title: "Failed to create property", description: await getApiErrorMessage(error), variant: "destructive" });
        },
    });

    const updateContactMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateContactRequest }) =>
            apiClient.contacts.updateContact({ id, updateContactRequest: data }),
        onSuccess: () => invalidateContacts(),
        onError: async (error: unknown) => {
            toast({ title: "Failed to update contact", description: await getApiErrorMessage(error), variant: "destructive" });
        },
    });
    const updateOrgMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateOrganizationRequest }) =>
            apiClient.organizations.updateOrganization({ id, updateOrganizationRequest: data }),
        onSuccess: () => invalidateOrganizations(),
        onError: async (error: unknown) => {
            toast({ title: "Failed to update organization", description: await getApiErrorMessage(error), variant: "destructive" });
        },
    });
    const updatePropertyMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdatePropertyRequest }) =>
            apiClient.properties.updateProperty({ id, updatePropertyRequest: data }),
        onSuccess: () => invalidateProperties(),
        onError: async (error: unknown) => {
            toast({ title: "Failed to update property", description: await getApiErrorMessage(error), variant: "destructive" });
        },
    });

    const searchContacts = useCallback(async (query: string): Promise<LinkableEntity[]> => {
        const res = await apiClient.contacts.listContacts({ search: query || undefined, limit: 15 });
        return (res.contacts || []).map((c) => {
            if (c.contactType) contactTypeCache.current.set(c.id, c.contactType);
            return {
                id: c.id,
                label: [c.firstName, c.lastName].filter(Boolean).join(" ") || `Contact #${c.id}`,
                badge: c.contactType,
            };
        });
    }, []);

    const searchOrganizations = useCallback(async (query: string): Promise<LinkableEntity[]> => {
        const res = await apiClient.organizations.listOrganizations({ search: query || undefined, limit: 15 });
        return (res.organizations || []).map((o) => {
            if (o.orgType) orgTypeCache.current.set(o.id, o.orgType);
            return { id: o.id, label: o.name || `Org #${o.id}`, badge: o.orgType };
        });
    }, []);

    const searchProperties = useCallback(async (query: string): Promise<LinkableEntity[]> => {
        const res = await apiClient.properties.listProperties({ search: query || undefined, limit: 15 });
        return (res.properties || []).map((p) => ({
            id: p.id,
            label: [p.streetAddress, p.unit, p.city, p.state, p.zipCode].filter(Boolean).join(", ") || `Property #${p.id}`,
        }));
    }, []);

    const openContactDetail = useCallback(async (id: number) => {
        setDetailContact(null);
        setDetailContactIsLocal(false);
        try {
            const linkedContact = await apiClient.claims.getClaimLinkedContact({ claimId, contactId: id });
            setDetailContact(linkedContact);
            setDetailContactIsLocal(viewerCompanyId != null && linkedContact.companyId === viewerCompanyId);
        } catch (error) {
            toast({ title: "Failed to load contact", description: await getApiErrorMessage(error), variant: "destructive" });
        }
    }, [claimId, toast, viewerCompanyId]);
    const openOrgDetail = useCallback(async (id: number) => {
        setDetailOrg(null);
        setDetailOrgIsLocal(false);
        try {
            const linkedOrganization = await apiClient.claims.getClaimLinkedOrganization({ claimId, organizationId: id });
            setDetailOrg(linkedOrganization);
            setDetailOrgIsLocal(viewerCompanyId != null && linkedOrganization.companyId === viewerCompanyId);
        } catch (error) {
            toast({ title: "Failed to load organization", description: await getApiErrorMessage(error), variant: "destructive" });
        }
    }, [claimId, toast, viewerCompanyId]);
    const openPropertyDetail = useCallback(async (id: number) => {
        setDetailProperty(null);
        setDetailPropertyIsLocal(false);
        try {
            const linkedProperty = await apiClient.claims.getClaimLinkedProperty({ claimId, propertyId: id });
            setDetailProperty(linkedProperty);
            setDetailPropertyIsLocal(viewerCompanyId != null && linkedProperty.companyId === viewerCompanyId);
        } catch (error) {
            toast({ title: "Failed to load property", description: await getApiErrorMessage(error), variant: "destructive" });
        }
    }, [claimId, toast, viewerCompanyId]);

    return {
        canWrite,
        canManage,
        contacts: {
            items: contacts.map((c) => ({
                key: c.id,
                label: [c.firstName, c.lastName].filter(Boolean).join(" ") || `Contact #${c.contactId}`,
                onNavigate: () => openContactDetail(c.contactId),
                onUnlink: canWrite ? () => unlinkContact(c.contactId) : undefined,
            })),
            existingIds: contacts.map((c) => c.contactId),
            onSearch: searchContacts,
            onSelect: (id) => linkContact({ contactId: id, role: contactTypeCache.current.get(id) || "Other" }),
            onCreateNew: () => setContactCreateOpen(true),
        },
        organizations: {
            items: organizations.map((o) => ({
                key: o.id,
                label: o.organizationName || `Org #${o.organizationId}`,
                onNavigate: () => openOrgDetail(o.organizationId),
                onUnlink: canWrite ? () => unlinkOrganization(o.organizationId) : undefined,
            })),
            existingIds: organizations.map((o) => o.organizationId),
            onSearch: searchOrganizations,
            onSelect: (id) => linkOrganization({ organizationId: id, role: orgTypeCache.current.get(id) || "Other" }),
            onCreateNew: () => setOrgCreateOpen(true),
        },
        properties: {
            items: properties.map((p) => ({
                key: p.id,
                label:
                    [p.streetAddress, p.city, p.state, p.zipCode].filter(Boolean).join(", ") ||
                    `Property #${p.propertyId}`,
                badge: p.addressType,
                onNavigate: () => openPropertyDetail(p.propertyId),
                onUnlink: canWrite ? () => unlinkProperty({ propertyId: p.propertyId, addressType: p.addressType }) : undefined,
            })),
            existingIds: properties.map((p) => p.propertyId),
            onSearch: searchProperties,
            onSelect: (id) => linkProperty(id),
            onCreateNew: () => setPropertyCreateOpen(true),
        },
        dialogs: {
            contact: {
                createOpen: contactCreateOpen,
                setCreateOpen: setContactCreateOpen,
                create: createContactMutation.mutateAsync,
                isCreating: createContactMutation.isPending,
                onCreated: invalidateContacts,
                detail: detailContact,
                setDetail: setDetailContact,
                detailIsLocal: detailContactIsLocal,
                setDetailIsLocal: setDetailContactIsLocal,
                edit: editContact,
                setEdit: setEditContact,
                editIsLocal: editContactIsLocal,
                setEditIsLocal: setEditContactIsLocal,
                update: updateContactMutation.mutateAsync,
                isUpdating: updateContactMutation.isPending,
            },
            organization: {
                createOpen: orgCreateOpen,
                setCreateOpen: setOrgCreateOpen,
                create: createOrgMutation.mutateAsync,
                isCreating: createOrgMutation.isPending,
                onCreated: invalidateOrganizations,
                detail: detailOrg,
                setDetail: setDetailOrg,
                detailIsLocal: detailOrgIsLocal,
                setDetailIsLocal: setDetailOrgIsLocal,
                edit: editOrg,
                setEdit: setEditOrg,
                editIsLocal: editOrgIsLocal,
                setEditIsLocal: setEditOrgIsLocal,
                update: updateOrgMutation.mutateAsync,
                isUpdating: updateOrgMutation.isPending,
            },
            property: {
                createOpen: propertyCreateOpen,
                setCreateOpen: setPropertyCreateOpen,
                create: createPropertyMutation.mutateAsync,
                isCreating: createPropertyMutation.isPending,
                onCreated: invalidateProperties,
                detail: detailProperty,
                setDetail: setDetailProperty,
                detailIsLocal: detailPropertyIsLocal,
                setDetailIsLocal: setDetailPropertyIsLocal,
                edit: editProperty,
                setEdit: setEditProperty,
                editIsLocal: editPropertyIsLocal,
                setEditIsLocal: setEditPropertyIsLocal,
                update: updatePropertyMutation.mutateAsync,
                isUpdating: updatePropertyMutation.isPending,
            },
        },
    };
}
